import { useQuery } from '@tanstack/react-query';
import { TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { Card } from '../../../components/ui/card';
import { DateRangePicker } from '../../../components/ui/date-range-picker';
import { partnerApi } from '../../../lib/api';

interface MeasureMetricsData {
  summary: {
    activeSmbs: number;
    totalSpend: number;
  };
  dailySpend: Array<{
    smbName: string;
    platformName: string;
    adsSpend: number;
    revenue: number;
    roas: number;
  }>;
  needsSetup: Array<{
    businessName: string;
    platformName: string;
    website: string | null;
    dataIntegration: string;
    websiteTracking: string;
    connectedAdAccounts: number;
    adPlatforms: string;
    firstConnectedAt: string;
  }>;
}

export const MeasureMetrics = () => {
  const [selectedDays, setSelectedDays] = useState<number>(0); // 0 = All Time

  const {
    data: metrics,
    isLoading,
    isFetching,
  } = useQuery<MeasureMetricsData>({
    queryKey: ['measure-metrics', selectedDays],
    queryFn: () => partnerApi.getMeasureMetrics(selectedDays),
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: previousData => previousData,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading metrics...</div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">No data available</div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatCompactCurrency = (amount: number) => {
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    }
    return formatCurrency(amount);
  };

  return (
    <div className="space-y-6">
      <DateRangePicker
        selectedDays={selectedDays}
        onSelectDays={setSelectedDays}
        isLoading={isFetching}
      />

      {/* Summary Cards */}
      <div
        className={`grid grid-cols-1 md:grid-cols-2 gap-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
        {/* Active Measure SMBs */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Active Measure SMBs
              </p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {metrics.summary.activeSmbs}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </Card>

        {/* Total Spend */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Measure Spend (
                {selectedDays === 0 ? 'All Time' : `Last ${selectedDays}d`})
              </p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {formatCompactCurrency(metrics.summary.totalSpend)}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Daily Spend Table */}
      {metrics.dailySpend && metrics.dailySpend.length > 0 && (
        <Card
          className={`p-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              <TrendingUp className="inline h-5 w-5 text-blue-600 mr-2" />
              Measure SMB Daily Spend (
              {selectedDays === 0 ? 'All Time' : `${selectedDays} Days`})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Smb name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total ads spend ($)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue ($)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ROAS
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {metrics.dailySpend.map((spend, index) => (
                  <tr
                    key={`${spend.smbName}-${index}`}
                    className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {spend.smbName}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {spend.adsSpend.toFixed(2)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {spend.revenue.toFixed(2)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {spend.roas.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Partners Who Need Setup Table */}
      {metrics.needsSetup.length > 0 && (
        <Card
          className={`p-6 transition-all duration-300 ${isFetching ? 'opacity-75 scale-[0.98]' : 'opacity-100 scale-100'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              <TrendingUp className="inline h-5 w-5 text-blue-600 mr-2" />
              Tenants Who Need Setup
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Business Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Website
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data Integration
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Website Tracking
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Connected Ad Accounts
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ad Platforms
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    First Connected At
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {metrics.needsSetup.map((business, index) => (
                  <tr
                    key={`${business.businessName}-${index}`}
                    className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {business.businessName}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {business.website || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          business.dataIntegration === 'Set'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                        {business.dataIntegration}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          business.websiteTracking === 'Set'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                        {business.websiteTracking}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {business.connectedAdAccounts}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {business.adPlatforms}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {business.firstConnectedAt}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};
